import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Copy, CheckCircle2, TrendingUp, Users, Share2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoyaltyPanel = () => {
  const [data, setData] = useState(null);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeemPoints, setRedeemPoints] = useState(100);
  const [redeeming, setRedeeming] = useState(false);
  const [justRedeemed, setJustRedeemed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refCopied, setRefCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, ref] = await Promise.all([
        axios.get(`${API}/loyalty/me`, { withCredentials: true }),
        axios.get(`${API}/loyalty/referral`, { withCredentials: true }),
      ]);
      setData(me.data);
      setReferral(ref.data);
      setRedeemPoints(Math.min(100, me.data.points));
    } catch {
      toast.error("Impossible de charger votre fidélité");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doRedeem = async () => {
    if (!data) return;
    if (redeemPoints < data.redeem.min_points || redeemPoints > data.points) return;
    setRedeeming(true);
    try {
      const { data: res } = await axios.post(
        `${API}/loyalty/redeem`,
        { points: redeemPoints },
        { withCredentials: true }
      );
      setJustRedeemed(res);
      toast.success(`Code créé : ${res.discount_eur}€ à utiliser`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'échange");
    } finally {
      setRedeeming(false);
    }
  };

  const copyCode = async () => {
    if (!justRedeemed?.code) return;
    try {
      await navigator.clipboard.writeText(justRedeemed.code);
      setCopied(true);
      toast.success("Code copié");
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  const copyReferral = async () => {
    if (!referral?.share_url) return;
    try {
      await navigator.clipboard.writeText(referral.share_url);
      setRefCopied(true);
      toast.success("Lien de parrainage copié");
      setTimeout(() => setRefCopied(false), 2000);
    } catch { /* noop */ }
  };

  if (loading) return <p className="text-white/40 text-center py-12">Chargement…</p>;
  if (!data) return null;

  const canRedeem = data.points >= data.redeem.min_points;
  const nextTierPct = data.next_tier
    ? Math.min(100, Math.round((data.lifetime_points / data.next_tier.min_points) * 100))
    : 100;

  return (
    <div data-testid="loyalty-panel" className="space-y-10">
      {/* Balance card */}
      <div className="border border-[color:var(--gold)]/30 bg-gradient-to-b from-[#0a0a0a] to-[#050505] p-8 md:p-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 opacity-10">
          <Sparkles size={220} className="text-[color:var(--gold)]" strokeWidth={0.5} />
        </div>
        <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-4">
          Programme Le Cercle Douba · Statut {data.tier.label}
        </p>
        <div className="flex items-baseline gap-3">
          <p data-testid="loyalty-balance" className="font-serif text-6xl md:text-7xl text-white">
            {data.points}
          </p>
          <p className="text-white/50 text-sm">points disponibles</p>
        </div>
        <p className="text-white/40 text-xs mt-2">
          Cumul total à vie : <span className="text-white/70">{data.lifetime_points} pts</span>
          <span className="mx-2">·</span>
          <span className="text-[color:var(--gold-soft)]">{data.tier.perk}</span>
        </p>

        {data.next_tier && (
          <div className="mt-8">
            <div className="flex items-center justify-between text-[10px] label-caps tracking-[0.25em] text-white/50 mb-2">
              <span>Vers <em className="italic not-italic text-[color:var(--gold)]">{data.next_tier.label}</em></span>
              <span>{data.next_tier.points_needed} pts restants</span>
            </div>
            <div className="h-1 bg-white/10 relative overflow-hidden">
              <div
                data-testid="loyalty-progress"
                className="absolute inset-y-0 left-0 bg-[color:var(--gold)] transition-all duration-700"
                style={{ width: `${nextTierPct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Redeem */}
      <div className="border border-white/10 p-8">
        <p className="label-caps text-[color:var(--gold)] text-[10px] mb-4">Échanger mes points</p>
        <h3 className="font-serif text-2xl md:text-3xl mb-2">
          {data.redeem.step} pts = <em className="italic text-[color:var(--gold-soft)]">{data.redeem.eur_per_100}€</em> offerts
        </h3>
        <p className="text-white/50 text-sm mb-6 font-light">
          Générez un code promo unique à utiliser sur votre prochaine commande. Minimum {data.redeem.min_points} points par tranche.
        </p>

        {canRedeem ? (
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-[10px] label-caps text-white/40 mb-2">Points à échanger</label>
              <input
                data-testid="loyalty-redeem-input"
                type="number"
                min={data.redeem.min_points}
                max={Math.floor(data.points / data.redeem.step) * data.redeem.step}
                step={data.redeem.step}
                value={redeemPoints}
                onChange={(e) => setRedeemPoints(parseInt(e.target.value, 10) || 0)}
                className="input-line w-40 text-lg"
              />
              <p className="text-[color:var(--gold)] text-sm mt-2">
                = {((redeemPoints / 100) * data.redeem.eur_per_100).toFixed(2)}€ offerts
              </p>
            </div>
            <button
              data-testid="loyalty-redeem-btn"
              onClick={doRedeem}
              disabled={redeeming || redeemPoints > data.points || redeemPoints % data.redeem.step !== 0}
              className="btn-gold"
            >
              {redeeming ? "..." : "Générer mon code"}
            </button>
          </div>
        ) : (
          <p className="text-white/50 text-sm italic">
            Encore {data.redeem.min_points - data.points} points pour votre première remise.
            Chaque euro dépensé = 1 point.
          </p>
        )}

        {justRedeemed && (
          <div data-testid="loyalty-redeem-result" className="mt-6 border border-[color:var(--gold)] p-5 bg-[color:var(--gold)]/[0.04]">
            <p className="label-caps text-[10px] text-[color:var(--gold)] mb-3">Votre code fidélité</p>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="font-mono text-2xl tracking-[0.2em] text-white">{justRedeemed.code}</p>
              <button
                onClick={copyCode}
                data-testid="loyalty-copy-code"
                className="btn-outline flex items-center gap-2 py-2 px-4"
              >
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                {copied ? "Copié" : "Copier"}
              </button>
            </div>
            <p className="text-white/50 text-xs mt-3">
              Remise de <span className="text-[color:var(--gold)]">{justRedeemed.discount_eur}€</span> · à saisir au moment du paiement · valable sur votre prochaine commande.
            </p>
          </div>
        )}
      </div>

      {/* Referral / parrainage */}
      {referral && (
        <div className="border border-white/10 p-8" data-testid="referral-block">
          <div className="flex items-center gap-2 mb-4">
            <Users size={12} className="text-[color:var(--gold)]" strokeWidth={1.5} />
            <p className="label-caps text-[color:var(--gold)] text-[10px]">Programme de parrainage</p>
          </div>
          <h3 className="font-serif text-2xl md:text-3xl mb-2">
            Parrainez, gagnez <em className="italic text-[color:var(--gold-soft)]">{referral.bonus_points} pts</em>
          </h3>
          <p className="text-white/50 text-sm mb-6 font-light">
            Pour chaque ami·e qui passe sa première commande via votre lien, vous recevez
            <span className="text-[color:var(--gold)]"> {referral.bonus_points} points </span>
            et il bénéficie de <span className="text-[color:var(--gold)]">−{referral.referred_discount_percent}%</span> supplémentaires.
          </p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="border border-white/10 p-4 text-center">
              <p className="label-caps text-[9px] text-white/40 tracking-[0.2em] mb-1">Filleuls</p>
              <p className="font-serif text-3xl text-white" data-testid="referral-count">{referral.sponsored_count}</p>
            </div>
            <div className="border border-white/10 p-4 text-center">
              <p className="label-caps text-[9px] text-white/40 tracking-[0.2em] mb-1">Points gagnés</p>
              <p className="font-serif text-3xl text-[color:var(--gold)]">{referral.points_earned}</p>
            </div>
            <div className="border border-white/10 p-4 text-center">
              <p className="label-caps text-[9px] text-white/40 tracking-[0.2em] mb-1">Bonus / filleul</p>
              <p className="font-serif text-3xl text-white">{referral.bonus_points}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-stretch">
            <div className="flex-1 bg-black/40 border border-white/10 px-4 py-3 flex items-center overflow-hidden">
              <p className="font-mono text-sm text-white/80 truncate" data-testid="referral-url">{referral.share_url}</p>
            </div>
            <button
              data-testid="referral-copy"
              onClick={copyReferral}
              className="btn-outline flex items-center justify-center gap-2 px-6"
            >
              {refCopied ? <CheckCircle2 size={14} /> : <Share2 size={14} />}
              {refCopied ? "Copié" : "Copier le lien"}
            </button>
          </div>
          <p className="text-white/40 text-xs mt-4 italic">
            Code : <span className="text-[color:var(--gold)] font-mono">{referral.code}</span> ·
            Partagez-le par WhatsApp, SMS ou email — vos filleuls saisissent ce code à l'inscription.
          </p>
        </div>
      )}

      {/* Tiers */}
      <div>
        <p className="label-caps text-[color:var(--gold)] text-[10px] mb-4">Les statuts du Cercle</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {["decouverte", "cercle", "signature", "prestige"].map((k, i) => {
            const tiers = [
              { key: "decouverte", label: "Découverte", min_points: 0, perk: "Bienvenue" },
              { key: "cercle", label: "Le Cercle", min_points: 250, perk: "-5% permanent" },
              { key: "signature", label: "Signature", min_points: 750, perk: "-10% + avant-première" },
              { key: "prestige", label: "Prestige", min_points: 2000, perk: "-15% + coffret annuel" },
            ];
            const t = tiers[i];
            const isActive = data.tier.key === t.key;
            return (
              <div
                key={t.key}
                data-testid={`loyalty-tier-${t.key}`}
                className={`border p-5 ${isActive ? "border-[color:var(--gold)] bg-[color:var(--gold)]/[0.04]" : "border-white/10"}`}
              >
                <p className={`font-serif text-xl mb-1 ${isActive ? "text-[color:var(--gold)]" : "text-white"}`}>{t.label}</p>
                <p className="text-white/40 text-[10px] label-caps mb-3">Dès {t.min_points} pts</p>
                <p className="text-white/60 text-xs">{t.perk}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* History */}
      {data.history && data.history.length > 0 && (
        <div>
          <p className="label-caps text-[color:var(--gold)] text-[10px] mb-4 flex items-center gap-2">
            <TrendingUp size={12} /> Historique
          </p>
          <div className="border border-white/10 divide-y divide-white/5">
            {data.history.slice(0, 20).map((tx, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between text-sm">
                <div>
                  <p className="text-white/80">{tx.reason || (tx.type === "earned" ? "Points gagnés" : "Points utilisés")}</p>
                  <p className="text-white/40 text-xs mt-0.5">
                    {new Date(tx.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
                <p className={`font-mono ${tx.points >= 0 ? "text-[color:var(--gold)]" : "text-white/50"}`}>
                  {tx.points >= 0 ? "+" : ""}{tx.points} pts
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyPanel;
